import React, { useState } from "react";
import { API, copyText } from "@/lib/api";
import { toast } from "sonner";
import { X, DownloadSimple, ShareNetwork, FacebookLogo, InstagramLogo, LinkSimple } from "@phosphor-icons/react";

// Generic branded share-card modal: Instagram (1:1) / Facebook (16:9) preview, download, native share, FB, copy link.
export function ShareCardModal({ title, cardPath, sharePath, fileName, shareText, prefix, onClose }) {
  const [size, setSize] = useState("square");
  const [busy, setBusy] = useState(false);
  const cardUrl = (s) => `${API}${cardPath}${cardPath.includes("?") ? "&" : "?"}size=${s}&t=${Date.now()}`;
  const shareUrl = `${API}${sharePath}`;

  const fetchBlob = async () => {
    const res = await fetch(cardUrl(size));
    if (!res.ok) throw new Error();
    return res.blob();
  };

  const download = async () => {
    setBusy(true);
    try {
      const url = URL.createObjectURL(await fetchBlob());
      const a = document.createElement("a");
      a.href = url; a.download = `${fileName}-${size}.png`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Card downloaded");
    } catch { toast.error("Could not download the card"); }
    finally { setBusy(false); }
  };

  const nativeShare = async () => {
    setBusy(true);
    try {
      const file = new File([await fetchBlob()], `${fileName}.png`, { type: "image/png" });
      if (navigator.canShare && navigator.canShare({ files: [file] })) { await navigator.share({ files: [file], title, text: shareText }); return; }
      if (navigator.share) { await navigator.share({ title, text: shareText, url: shareUrl }); return; }
      const ok = await copyText(shareUrl);
      toast[ok ? "success" : "error"](ok ? "Share link copied" : "Could not share");
    } catch (e) {
      if (e && e.name === "AbortError") return;
      toast.error("Sharing isn't available on this device — try Download instead");
    } finally { setBusy(false); }
  };

  const openFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank", "noopener,width=640,height=640");
  const copyLink = async () => {
    const ok = await copyText(shareUrl);
    toast[ok ? "success" : "error"](ok ? "Share link copied" : "Could not copy link");
  };

  const sizeBtn = (key, Icon, label) => (
    <button onClick={() => setSize(key)} data-testid={`${prefix}-share-size-${key}`}
      className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-xs uppercase font-bold tracking-wide border transition-colors ${size === key ? "bg-[#FF5722] text-black border-[#FF5722]" : "border-white/15 text-white/60 hover:text-white"}`}>
      <Icon size={16} weight="bold" /> {label}
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={onClose} data-testid={`${prefix}-share-modal`}>
      <div className="bg-[#121212] border border-white/10 w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
          <h3 className="font-display text-xl uppercase flex items-center gap-2"><ShareNetwork size={20} weight="fill" className="text-[#FF5722]" /> {title}</h3>
          <button onClick={onClose} data-testid={`${prefix}-share-close`} className="text-white/50 hover:text-white"><X size={20} /></button>
        </div>
        <div className="p-5 space-y-4">
          <div className="flex gap-2">
            {sizeBtn("square", InstagramLogo, "Instagram (1:1)")}
            {sizeBtn("wide", FacebookLogo, "Facebook (16:9)")}
          </div>
          <div className="bg-[#0A0A0A] border border-white/10 p-2 flex items-center justify-center">
            <img key={size} src={cardUrl(size)} alt={`${title} share card`} data-testid={`${prefix}-share-preview`} className={`w-full ${size === "square" ? "max-w-sm" : ""} object-contain`} />
          </div>
          <p className="text-xs text-white/40 leading-relaxed">
            Instagram doesn't allow direct web posting — tap <span className="text-white/70 font-semibold">Download</span> then upload the image to your story or feed. For Facebook, use <span className="text-white/70 font-semibold">Facebook</span> to post with a live preview.
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={download} disabled={busy} data-testid={`${prefix}-share-download`} className="flex items-center justify-center gap-2 bg-[#FF5722] hover:bg-[#FF7043] disabled:opacity-50 text-black font-bold uppercase tracking-wide text-sm py-3 transition-colors">
              <DownloadSimple size={18} weight="bold" /> Download
            </button>
            <button onClick={nativeShare} disabled={busy} data-testid={`${prefix}-share-native`} className="flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white/80 font-bold uppercase tracking-wide text-sm py-3 transition-colors">
              <ShareNetwork size={18} weight="bold" /> Share
            </button>
            <button onClick={openFacebook} data-testid={`${prefix}-share-facebook`} className="flex items-center justify-center gap-2 border border-[#1877F2]/60 text-[#4a90ff] hover:bg-[#1877F2]/10 font-bold uppercase tracking-wide text-sm py-3 transition-colors">
              <FacebookLogo size={18} weight="fill" /> Facebook
            </button>
            <button onClick={copyLink} data-testid={`${prefix}-share-copy`} className="flex items-center justify-center gap-2 border border-white/20 hover:border-white text-white/80 font-bold uppercase tracking-wide text-sm py-3 transition-colors">
              <LinkSimple size={18} weight="bold" /> Copy Link
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ShootoutShareModal({ champion, onClose }) {
  const text = champion
    ? `${champion.name} is the IEFSC 3-Point Shootout Champion with ${champion.final_points} pts!`
    : "IEFSC 3-Point Shootout — live results";
  return <ShareCardModal title="Share Shootout Card" cardPath="/shootout/card.png" sharePath="/share/shootout" fileName="iefsc-3pt-shootout" shareText={text} prefix="shootout" onClose={onClose} />;
}

export const ShareCardButton = ({ onClick, testid, label = "Share Card", className = "" }) => (
  <button onClick={onClick} data-testid={testid} className={`flex items-center gap-1.5 border border-[#FF5722]/50 text-[#FF5722] hover:bg-[#FF5722]/10 uppercase tracking-wide text-xs font-bold px-3 py-2 transition-all duration-200 ${className}`}>
    <ShareNetwork size={15} weight="fill" /> {label}
  </button>
);
